/**
 * The in-flight gesture record for the canvas.
 *
 * Split out of the canvas component so the component keeps only wiring and this
 * file holds the bookkeeping a drag needs: where it started, what each node's
 * rect was at the start, and which rects it may snap to.
 *
 * Pure data + pure helpers — no Angular, no DOM.
 */

import type {
  DesignNode,
  Point,
  Rect,
  ResizeHandle,
} from '../../design-builder.types';
import { rectOf } from '../../utils/design-geometry.utils';

export type GestureKind = 'move' | 'resize' | 'rotate' | 'marquee' | 'pan' | 'draw';

export interface ActiveGesture {
  kind: GestureKind;
  /** Pointer position where the gesture started, in SCREEN px. */
  originScreen: Point;
  /** Same point in DOCUMENT space. */
  originDoc: Point;
  /** Starting rect per node id — the basis every delta is applied to. */
  startRects: Map<string, Rect>;
  /** Immutable geometry source for group scaling throughout the gesture. */
  startNodes: Map<string, DesignNode>;
  /** Ids the gesture moves. */
  nodeIds: string[];
  /** Resize only. */
  handle?: ResizeHandle;
  /** Rotate only — the selection's centre in document space. */
  center?: Point;
  /** Rotate only — the node's rotation when the gesture began. */
  startRotation?: number;
  /** Rotate only — pointer angle at the start, so rotation is relative. */
  startAngle?: number;
  /** Rects the moving nodes may snap to (siblings + the parent frame). */
  snapTargets: Rect[];
  /** Pan only — viewport pan at the start. */
  startPan?: Point;
  /** True once the pointer has moved past the slop threshold. */
  moved: boolean;
  /**
   * Move only — pressed on a widget's button / link: no pointer capture yet, so a
   * plain click still reaches it. Past the slop it becomes a drag (captured then).
   */
  deferredCapture?: boolean;
  /** Swallows the click that ends a drag started on a button, so the drag does not press it. */
  clickGuard?: (event: Event) => void;
}

/** Pointer travel (screen px) before a press counts as a drag rather than a click. */
export const DRAG_SLOP_PX = 3;

export function createGesture(
  kind: GestureKind,
  originScreen: Point,
  originDoc: Point,
  nodes: DesignNode[],
  snapTargets: Rect[] = [],
): ActiveGesture {
  return {
    kind,
    originScreen,
    originDoc,
    nodeIds: nodes.map((node) => node.id),
    startRects: new Map(nodes.map((node) => [node.id, rectOf(node)])),
    startNodes: new Map(nodes.map((node) => [node.id, structuredClone(node)])),
    snapTargets,
    moved: false,
  };
}

/** True once the pointer has travelled far enough to be a real drag. */
export function passedSlop(gesture: ActiveGesture, screen: Point): boolean {
  if (gesture.moved) return true;
  const dx = screen.x - gesture.originScreen.x;
  const dy = screen.y - gesture.originScreen.y;
  return Math.hypot(dx, dy) >= DRAG_SLOP_PX;
}

/** Angle in degrees from `center` to `point`, measured clockwise from 12 o'clock. */
export function angleFrom(center: Point, point: Point): number {
  return (Math.atan2(point.y - center.y, point.x - center.x) * 180) / Math.PI + 90;
}

/**
 * Rects a moving node may snap to: every visible sibling it is not itself, plus
 * the parent's own box (so nodes snap to the frame edges too).
 *
 * Coordinates are in the MOVING node's space — siblings already are, and the
 * parent contributes a box at the origin with its own size.
 */
export function collectSnapTargets(
  siblings: DesignNode[],
  movingIds: ReadonlySet<string>,
  parent: DesignNode | null,
  canvasSize: { width: number; height: number },
): Rect[] {
  const targets: Rect[] = siblings
    .filter((node) => node.visible && !movingIds.has(node.id))
    .map(rectOf);

  targets.push(
    parent
      ? { x: 0, y: 0, width: parent.width, height: parent.height }
      : { x: 0, y: 0, width: canvasSize.width, height: canvasSize.height },
  );
  return targets;
}
